<template>
  <DSOptionLabel
    :option="props.option"
    :disabled="props.disabled"
    :selected="isChecked"
    :direction="props.direction"
    :has-error="hasErrors"
    :tooltip="props.tooltip"
    :tag="props.labelTag"
    :class="DS_CHECKBOX_CLASSES.Root"
  >
    <template #default="defaultSlotAttributes">
      <input
        v-if="shouldRenderUncheckedInput"
        type="hidden"
        :name="props.name"
        :value="props.uncheckedValue"
        :disabled="props.disabled"
      />
      <input
        ref="inputRef"
        type="checkbox"
        :name="props.name"
        :value="checkedSubmitValue"
        :checked="isChecked"
        :disabled="props.disabled"
        :class="[DS_CHECKBOX_CLASSES.Input, defaultSlotAttributes.class]"
        :data-cy="`ds-checkbox__input--${props.option.value || 'null'}`"
        @change="onToggle"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
    </template>

    <template
      v-if="slots.label"
      #label
    >
      <slot name="label" />
    </template>
  </DSOptionLabel>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSCheckboxEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSOptionLabel from "@/src/design_system/form_elements/shared_modules/option_label/components/DSOptionLabel.vue";
import type { DSOptionLabelSlots } from "@/src/design_system/form_elements/shared_modules/option_label/types/slots";
import { DSCheckboxProps } from "@/src/design_system/form_elements/checkbox/types/props";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import { DS_CHECKBOX_CLASSES } from "@/src/design_system/form_elements/checkbox/constants/classes";
import { useBooleanField } from "@/src/design_system/form_elements/shared/composables/useBooleanField";

const props = withDefaults(defineProps<DSCheckboxProps>(), {
  disabled: false,
  direction: DS_DIRECTION.Horizontally,
  hasError: false,
  tooltip: undefined,
  labelTag: "label",
});

const model = defineModel<boolean>({ default: false });
const slots = defineSlots<Pick<DSOptionLabelSlots, "label">>();
const emit = defineEmits<DSCheckboxEmits>();
const inputRef = ref<HTMLInputElement>();
const { focus, blur } = useDSInput(inputRef);

defineExpose({ focus, blur });

const hasErrors = computed(() => props.hasError || !!props.errorMessages?.length);

const { isChecked, checkedSubmitValue, shouldRenderUncheckedInput, onToggle } = useBooleanField({
  model,
  name: computed(() => props.name),
  checkedValue: computed(() => props.checkedValue),
  defaultCheckedValue: computed(() => props.option.value),
  uncheckedValue: computed(() => props.uncheckedValue),
});
</script>
